import { useMutation } from '@urql/vue';
import { useLoginStore } from 'store/login';
import { computed, ref } from 'vue';
import type { VueCookies } from 'vue-cookies';

import { type SendEvent, useEventHub } from '@/composables/eventHub';
import LOGIN_NOTIFICATION from '@/graphql/loginnotification.gql';
import { cryptoRandomString } from '@/lib/cryptoRandomString';
import type { FischerPlugin, SsrCookiesObject } from '@/lib/fischerPlugin';
import { useNotificationStore } from '@/store/pinia/notification';
import { getIsIds } from '@/utils/idsTools';
import parseTokenData from '@/utils/parseTokenData';

import { useCookies } from './cookies';
import { useFischer } from './fischerPlugin';

const cookieName = 'site-login-init';
export const usernameCookie = 'login-dialog-user';
export const loginDialogCookies = 'login-dialog';
export const loggedInMessage = 'logged-in';

export const siteLoginDebugKey = 'site-login-debug';
export const loginChannel = 'site-login';

export type KeyedContents = Record<string, string | number | null | undefined>;

type LogDebug = (...args: any[]) => void;
type GetCookies = () => KeyedContents;

export const getAuthorizationUrl = (queryParams: string = '') => {
  return `${import.meta.env.VITE_API_URL}/authorizationserver/oauth/authorize?${queryParams}`;
};

export const logDebugFunction: LogDebug = (isDebug: boolean, ...params: any[]) => {
  if (isDebug) {
    console.info(...params);
  }
};

export const getCookiesFunction = (vueCookies: VueCookies, logDebug: LogDebug) => {
  const cookies = vueCookies.get(cookieName) || {};
  logDebug('get cookies', cookies?.time);
  return cookies;
};

const removeOldestHalf = <T extends Record<string, any>>(obj: T): Partial<T> => {
  const entries = Object.entries(obj).filter(([key]) => key !== 'time');

  const sorted = entries.sort((a, b) => {
    const validUntilA = a[1]?.validUntil ?? 0;
    const validUntilB = b[1]?.validUntil ?? 0;
    return validUntilA - validUntilB;
  });

  const countToRemove = Math.floor(sorted.length / 2);

  const entriesToKeep = sorted.slice(countToRemove);

  const result: any = {};

  if ('time' in obj) {
    result.time = obj.time;
  }

  for (const [key, value] of entriesToKeep) {
    result[key] = value;
  }

  return result;
};

export const setCookiesFunction = async (
  fischer: FischerPlugin,
  logDebug: LogDebug,
  vueCookies: VueCookies,
  getCookies: GetCookies,
  criticalLoginFailure: (reason: string) => void,
  cookies: Record<string, any>,
) => {
  const time = Date.now();
  logDebug('setting cookies', cookieName, time);

  cookies.time = time;
  Object.keys(cookies).forEach((key) => {
    const until = cookies[key]?.validUntil;
    if (until && until < time) {
      delete cookies[key];
    }
  });
  const json = JSON.stringify(cookies);
  if (encodeURIComponent(json).length > 4000) {
    cookies = removeOldestHalf(cookies);
  }

  vueCookies.set(
    cookieName,
    cookies,
    '1h',
    '/',
    IS_SHOWROOM ? undefined : fischer.hostname(),
    fischer.isSecureConnection(),
    'Lax',
  );

  if (getCookies().time !== time) {
    console.info('cookie was not stored, waiting a moment');
    await new Promise((resolve) => setTimeout(resolve, 500));

    if (getCookies().time !== time) {
      criticalLoginFailure('Failed to set cookies');
      return false;
    }
  }

  logDebug('cookies set');
  return true;
};

export const handleDebugFlagFunction = (fischer: FischerPlugin): boolean => {
  if (
    fischer.query(siteLoginDebugKey) ||
    new RegExp(`(?:#|&)${siteLoginDebugKey}=true(?:&|$)`).test(window.location.hash)
  ) {
    // @ts-ignore
    window.activateLoginDebug?.(true);
    return true;
  }
  return false;
};

export const postAccessTokeninSessionFunction = async (
  logDebug: LogDebug,
  sendEvent: SendEvent,
  accessToken: string,
  idToken: string,
  refreshToken?: string,
): Promise<string> => {
  try {
    logDebug('posting token to sitecore...');
    const user: Record<string, unknown> = {
      AccessToken: accessToken,
      IdToken: idToken,
      IsIdsSession: getIsIds(),
    };
    if (refreshToken) {
      user.RefreshToken = refreshToken;
    }
    const response = await fetch('/webapi/shop/userapi/Login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ User: user }),
    });
    if (!response.ok) throw new Error(`Could not post token: ${response.status}`);
    const result = await response.json();
    const successPage = result?.LoginSuccessPage;
    logDebug('posted token to sitecore', successPage);

    // send eventHub tracking
    await sendEvent('login', {
      userId: parseTokenData(idToken).email,
    });

    return successPage;
  } catch (_error) {
    throw new Error();
  }
};

export const setDevTokenFunction = (
  vueCookies: VueCookies,
  fischer: FischerPlugin,
  accessToken: string,
  idToken: string,
) => {
  if (!IS_SHOWROOM) {
    return;
  }
  const { exp }: { exp: number } = parseTokenData(idToken);
  const ttl = exp - Date.now() / 1000;
  fischer.user.setIdToken(idToken, String(ttl));
  fischer.user.setAccessToken(accessToken, String(ttl));
};

export const useLogin = () => {
  const fischer = useFischer();
  const isDebug = ref<boolean>(false);
  const hasCriticalLoginFailure = ref<boolean>(false);
  const { cookies: vueCookies } = useCookies();
  const { sendEvent } = useEventHub();
  const loginStore = useLoginStore();
  const notificationStore = useNotificationStore();

  const { executeMutation: mutateLoginNotification } = useMutation(LOGIN_NOTIFICATION);

  const sendLoginNotification = async (accessToken: string, idToken: string) => {
    const tokenData = parseTokenData(idToken);
    const userId = tokenData?.sub;
    if (!userId) {
      console.error('Could not find userId for loginnotification');
      return;
    }

    const r = await mutateLoginNotification({
      baseSiteId: fischer.baseSiteId(),
      userId,
      accessToken: accessToken,
    });
    if (r.error) {
      console.error('login notification failed', r.error);
    }
    return r;
  };

  const checkIsValidRedirect = (redirect: string | null) => {
    if (IS_SSR) {
      return false;
    }

    if (!redirect) {
      return false;
    }

    // do not redirect to current page or we go in circles
    if (redirect.startsWith(window.location.pathname)) {
      return false;
    }
    if (redirect.startsWith(`${window.location.origin}/${window.location.pathname}`)) {
      return false;
    }

    const dialogMatch = /^dialog:(\/[^@]*)/.exec(redirect);
    if (dialogMatch) {
      if (isSpecialPage(dialogMatch[1])) {
        return fischer.pages.myAccount().url;
      }
      return dialogMatch[1];
    }

    return redirect;
  };

  const criticalLoginFailure = (...args: any[]): void => {
    console.error(...args);
    notificationStore.openGeneralErrorNotification();
    hasCriticalLoginFailure.value = true;
  };

  const getAuthorizeRedirect = (query?: string | null) => {
    if (!query) {
      return null;
    }
    const withoutStateAndNonce = query
      .replace(/(\?|&)state=[^&]*/, '')
      .replace(/(\?|&)nonce=[^&]*/, '');
    return `${withoutStateAndNonce}&state=retry`;
  };

  const authorizeRedirect = computed<string | null>(() => {
    const query = fischer.query('authorize_redirect');
    return getAuthorizeRedirect(query);
  });

  const logDebug: LogDebug = (...args: any[]): void => {
    if (isDebug.value) {
      console.info(...args);
    }
  };

  const getCookies = () => getCookiesFunction(vueCookies, logDebug);

  const setCookies = async (cookies: KeyedContents) =>
    setCookiesFunction(fischer, logDebug, vueCookies, getCookies, criticalLoginFailure, cookies);

  const postAccessTokenInSession = async (
    accessToken: string,
    idToken: string,
    refreshToken?: string,
  ): Promise<string> => {
    try {
      return await postAccessTokeninSessionFunction(
        logDebug,
        sendEvent,
        accessToken,
        idToken,
        refreshToken,
      );
    } catch (error) {
      criticalLoginFailure('Could not post access token', error);
      throw error;
    }
  };

  const isSpecialPage = (url: string): boolean =>
    [
      fischer.pages.registration()?.url,
      fischer.pages.registrationSuccess()?.url,
      fischer.pages.logout()?.url,
      fischer.pages.logoutSuccess()?.url,
      fischer.pages.login()?.url,
      fischer.pages.loginSuccessPage()?.url,
      fischer.pages.activation()?.url,
      fischer.pages.passwordForgotten()?.url,
      fischer.pages.assistedService()?.url,
    ].includes(url);

  const goToLoginPage = (
    state: string,
    nonce: string,
    scope: string,
    responseType: string,
    redirectUri?: string,
  ) => {
    logDebug('initializing authorization');
    try {
      const params: SsrCookiesObject = {
        response_type: responseType,
        client_id: fischer.getClientId(),
        redirect_uri:
          redirectUri || authorizeRedirect.value || String(window.location).split('#')[0],
        scope,
        state,
        nonce,
      };

      if (authorizeRedirect.value) {
        const searchParams = new URLSearchParams(authorizeRedirect.value.replace(/^.*\?/, ''));
        searchParams.forEach((key, value) => {
          if (!params[key]) {
            params[key] = value;
          }
        });
      }

      window.location.href = getAuthorizationUrl(String(new URLSearchParams(params)));
    } catch (error) {
      console.error('SSO login failed', error);
    }
  };

  const clearOldStates = (cookies: Record<string, { validUntil: number }>) => {
    const maxTime = Date.now();
    const keys = Object.keys(cookies);
    const allLoginKeysWithTimes: [validUntil: number, key: string][] = [];
    const maxKeys = 10; // cookies are limited to 4k in size, limit to 5 attempts (states + nonce)
    keys.forEach((key) => {
      if (/^(?:state|nonce)-/.test(key)) {
        const { validUntil } = cookies[key];
        if (validUntil > maxTime) {
          allLoginKeysWithTimes.push([validUntil, key]);
        } else {
          console.info('Removing outdated cookie', validUntil);

          delete cookies[key];
        }
      }
    });
    if (allLoginKeysWithTimes.length > maxKeys) {
      console.info('Max login cookies exceeded');
      allLoginKeysWithTimes.sort((a, b) => b[0] - a[0]);

      allLoginKeysWithTimes.slice(maxKeys).forEach((item) => delete cookies[item[1]]);
    }
  };

  const createTokenState = async (formPage?: string, data?: string) => {
    const { state: stateId, verifier, challenge } = await createChallenge();

    const search = new URLSearchParams(window.location.search);
    let redirect = search.get('redirect') || formPage || fischer.uri();
    [
      'redirect',
      'brand',
      'ui_locales',
      'authorize_redirect',
      'logged-in',
      'authentication_error',
    ].forEach((param) => search.delete(param));
    const searchString = search.toString();
    if (searchString && redirect && !redirect.includes('?')) {
      redirect = `${redirect}?${searchString}`;
    }

    if (redirect && window.location.hash && window.location.hash !== '#') {
      redirect = `${redirect}${window.location.hash}`;
    }

    const validUntil = Date.now() + 60 * 60 * 5 * 10000;
    const state: {
      redirect: string;
      validUntil: number;
      data?: string;
      verifier: string;
    } = { redirect, verifier, validUntil };
    if (data) {
      state.data = data;
    }

    const cookies = getCookies();
    clearOldStates(cookies);
    cookies[stateId] = state;
    if (!(await setCookies(cookies))) {
      throw new Error('Could not create token state');
    }

    return { stateId, challenge };
  };

  const getAuthParams = async (
    customFormPage?: boolean,
    dataParam?: string,
    forceLanguage?: string | false,
  ) => {
    let lang = forceLanguage || fischer.siteLanguage();
    if (lang === 'da') {
      lang = 'da-DK';
    }
    const loginPage = fischer.pages.login().url;
    if (!loginPage && !fischer.query('init-login')) {
      console.error('Login page URL is not configured. Aborting login.');
      return null;
    }
    const redirectUri = fischer.query('init-login') || `${fischer.hostUrlBase()}${loginPage}`;
    logDebug('redirecting to loginFormPage');
    const clientId = fischer.getClientId();

    let pathname = window.location.pathname;
    if (loginStore.forceStart || isSpecialPage(pathname)) {
      pathname = IS_SHOWROOM ? '/' : `/${lang.toLowerCase()}`;
    }

    let data = dataParam;
    if (!data) {
      const brand = fischer.query('brand');
      if (brand) {
        data = /@(.*)/.exec(brand)?.[1];
      }
    }

    const brand = customFormPage
      ? `dialog:${pathname}${data ? '@' + data : ''}`
      : clientId.split('-')[0];
    const { stateId, challenge } = await createTokenState(customFormPage ? brand : undefined, data);

    const queryParams = new URLSearchParams({
      client_id: clientId,
      code_challenge: challenge,
      code_challenge_method: 'S256',
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid',
      state: stateId,
      brand,
    });

    return { queryParams, brand };
  };

  const initializeAuthorization = async (
    customFormPage?: boolean,
    data?: string,
    forceLanguage?: string | false,
  ) => {
    if (fischer.query('code')) {
      logDebug('got a code, not re-initializing login');
      return;
    }
    if (fischer.app.isEditMode()) {
      console.debug('not logging in because of edit mode');
      return;
    }
    const authParams = await getAuthParams(customFormPage, data, forceLanguage);
    if (!authParams) {
      return;
    }
    const { queryParams, brand } = authParams;
    const newLocation = getAuthorizationUrl(String(queryParams));
    logDebug('new location', newLocation);
    if (customFormPage) {
      logDebug('setting login-dialog', brand);
      vueCookies.set(loginDialogCookies, brand);
    }
    if (fischer.query('debug-no-redirect')) {
      console.info('debug-no-redirect');
      return;
    }
    window.location.href = newLocation;
  };

  const setDevToken = (accessToken: string, idToken: string) =>
    setDevTokenFunction(vueCookies, fischer, accessToken, idToken);

  const watchTabLogins = (callback: () => void): BroadcastChannel | null => {
    try {
      const bc = new BroadcastChannel(loginChannel);
      bc.onmessage = (event) => {
        const [type] = event?.data || [];
        if (type === loggedInMessage) {
          callback();
        } else {
          console.error('unknown login bc event');
        }
      };
      logDebug('created login bc');
      return bc;
    } catch (error) {
      console.error('Could not create login bc', error);
      return null;
    }
  };

  const notifyLogin = () => {
    try {
      const bc = new BroadcastChannel(loginChannel);
      bc?.postMessage([loggedInMessage]);
      logDebug('notified login');
    } catch (error) {
      console.error('Could not notify login', error);
    }
  };

  const base64UrlEncode = (data: string): string => {
    return btoa(data).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  };

  const generateCodeChallenge = async (codeVerifier: string) => {
    const data = new TextEncoder().encode(codeVerifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    return base64UrlEncode(String.fromCodePoint(...new Uint8Array(digest)));
  };

  const createChallenge = async () => {
    const state = cryptoRandomString(16);
    const verifier = cryptoRandomString(100);
    const challenge = await generateCodeChallenge(verifier);

    return { state, verifier, challenge };
  };

  return {
    authorizeRedirect,
    checkIsValidRedirect,
    createChallenge,
    criticalLoginFailure,
    getAuthParams,
    getAuthorizationUrl,
    getAuthorizeRedirect,
    getCookies,
    goToLoginPage,
    hasCriticalLoginFailure,
    initializeAuthorization,
    isDebug,
    isSpecialPage,
    logDebug,
    notifyLogin,
    postAccessTokenInSession,
    sendLoginNotification,
    setCookies,
    setDevToken,
    watchTabLogins,
  };
};
